(() => {
  if (window.CiranovaSampleScroll) {
    window.CiranovaSampleScroll.init(document);
    return;
  }

  const IMAGE_SELECTOR =
    'img[src*="ciranova-sample-box-cutout"], img[srcset*="ciranova-sample-box-cutout"]';
  const instances = [];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  let frameRequested = false;

  const clamp = (value, minimum = 0, maximum = 1) =>
    Math.min(maximum, Math.max(minimum, value));

  const smoothstep = (start, end, value) => {
    const progress = clamp((value - start) / (end - start));
    return progress * progress * (3 - 2 * progress);
  };

  const assetUrl = (source, filename) => {
    const url = new URL(source, window.location.href);
    url.pathname = url.pathname.replace(/[^/]+$/, filename);
    url.search = '';
    return url.href;
  };

  const createImage = (source, className, width, height) => {
    const image = document.createElement('img');
    image.className = `ciranova-sample-unbox__object ${className}`;
    image.src = source;
    image.alt = '';
    image.width = width;
    image.height = height;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.draggable = false;
    image.setAttribute('aria-hidden', 'true');
    return image;
  };

  const build = (image) => {
    if (image.dataset.ciranovaScrollReady === 'true' || reduceMotion.matches) return;

    image.dataset.ciranovaScrollReady = 'true';

    const source = image.currentSrc || image.src;
    const replacementTarget = image.closest('picture') || image;
    const host = replacementTarget.parentElement;
    const root = document.createElement('div');

    root.className = 'ciranova-sample-unbox';
    root.dataset.ciranovaSampleScroll = '';
    root.setAttribute('role', 'img');
    root.setAttribute(
      'aria-label',
      image.alt || 'Ciranova sample box opening to reveal five finish samples'
    );
    root.innerHTML = `
      <div class="ciranova-sample-unbox__scene">
        <div class="ciranova-sample-unbox__surface" aria-hidden="true"></div>
        <div class="ciranova-sample-unbox__objects" aria-hidden="true">
          <span class="ciranova-sample-unbox__shadow ciranova-sample-unbox__shadow--1"></span>
          <span class="ciranova-sample-unbox__shadow ciranova-sample-unbox__shadow--2"></span>
          <span class="ciranova-sample-unbox__shadow ciranova-sample-unbox__shadow--3"></span>
          <span class="ciranova-sample-unbox__shadow ciranova-sample-unbox__shadow--4"></span>
          <span class="ciranova-sample-unbox__shadow ciranova-sample-unbox__shadow--5"></span>
        </div>
        <div class="ciranova-sample-unbox__meta">The sample ritual</div>
        <div class="ciranova-sample-unbox__counter">
          <span data-sample-counter>00 / 05</span>
        </div>
      </div>
    `;

    const scene = root.querySelector('.ciranova-sample-unbox__scene');
    const objects = root.querySelector('.ciranova-sample-unbox__objects');
    const fallback = document.createElement('img');
    fallback.className = 'ciranova-sample-unbox__fallback';
    fallback.src = source;
    fallback.alt = '';
    fallback.width = 1536;
    fallback.height = 1024;
    fallback.loading = 'lazy';
    fallback.decoding = 'async';
    fallback.setAttribute('aria-hidden', 'true');

    const box = createImage(
      assetUrl(source, 'ciranova-sample-box.webp'),
      'ciranova-sample-unbox__box',
      740,
      700
    );
    const bottleDimensions = [[130, 350], [140, 330], [135, 350], [135, 330], [150, 350]];
    const bottles = bottleDimensions.map(([width, height], index) =>
      createImage(
        assetUrl(source, `ciranova-sample-bottle-${index + 1}.png`),
        `ciranova-sample-unbox__bottle ciranova-sample-unbox__bottle--${index + 1}`,
        width,
        height
      )
    );

    scene.prepend(fallback);
    objects.append(...bottles, box);
    host?.classList.add('ciranova-sample-scroll-host');
    replacementTarget.replaceWith(root);

    const instance = {
      root,
      scene,
      box,
      bottles,
      shadows: [...root.querySelectorAll('.ciranova-sample-unbox__shadow')],
      counter: root.querySelector('[data-sample-counter]')
    };

    const cutouts = [box, ...bottles];
    const revealCutouts = () => {
      if (cutouts.every((cutout) => cutout.complete && cutout.naturalWidth > 0)) {
        root.classList.add('is-ready');
      }
    };

    cutouts.forEach((cutout) => {
      if (!cutout.complete) {
        cutout.addEventListener('load', revealCutouts, { once: true });
      }
    });
    revealCutouts();

    if (canHover.matches) {
      scene.addEventListener('pointermove', (event) => {
        const bounds = scene.getBoundingClientRect();
        const horizontal = ((event.clientX - bounds.left) / bounds.width) * 100;
        const vertical = ((event.clientY - bounds.top) / bounds.height) * 100;
        root.style.setProperty('--sample-light-x', `${horizontal.toFixed(1)}%`);
        root.style.setProperty('--sample-light-y', `${vertical.toFixed(1)}%`);
      });

      scene.addEventListener('pointerleave', () => {
        root.style.setProperty('--sample-light-x', '32%');
        root.style.setProperty('--sample-light-y', '30%');
      });
    }

    instances.push(instance);
  };

  const update = () => {
    frameRequested = false;
    const viewportHeight = window.innerHeight;
    const startOffsets = [0.08, 0.19, 0.3, 0.41, 0.52];
    const finalCenters = [0.546, 0.63, 0.711, 0.791, 0.875];
    const startRotation = [-13, -7, 1, 8, 14];

    instances.forEach((instance) => {
      if (!instance.root.isConnected) return;

      const bounds = instance.root.getBoundingClientRect();
      const sceneBounds = instance.scene.getBoundingClientRect();
      const entryPoint = viewportHeight * 0.88;
      const completionPoint = viewportHeight * 0.14;
      const progress = clamp(
        (entryPoint - bounds.top) / (entryPoint - completionPoint)
      );

      instance.root.style.setProperty('--sample-progress', progress.toFixed(4));
      instance.box.style.transform =
        `translate3d(${(-2 + progress * 2).toFixed(2)}%, ` +
        `${(1.5 - progress * 1.5).toFixed(2)}%, 0) ` +
        `scale(${(0.975 + progress * 0.025).toFixed(3)})`;

      let revealed = 0;

      instance.bottles.forEach((bottle, index) => {
        const bottleProgress = smoothstep(
          startOffsets[index],
          startOffsets[index] + 0.27,
          progress
        );
        const horizontal =
          (0.425 - finalCenters[index]) * sceneBounds.width * (1 - bottleProgress);
        const lift = -sceneBounds.height * 0.18 * (1 - bottleProgress);
        const arc = Math.sin(bottleProgress * Math.PI) * sceneBounds.height * -0.075;
        const scale = 0.56 + bottleProgress * 0.44;
        const rotation = startRotation[index] * (1 - bottleProgress);
        const opacity = smoothstep(0, 0.13, bottleProgress);

        bottle.style.opacity = opacity.toFixed(3);
        bottle.style.transform =
          `translate3d(${horizontal.toFixed(1)}px, ${(lift + arc).toFixed(1)}px, 0) ` +
          `scale(${scale.toFixed(3)}) rotate(${rotation.toFixed(2)}deg)`;

        instance.shadows[index].style.opacity =
          (bottleProgress * 0.24).toFixed(3);
        instance.shadows[index].style.transform =
          `scale(${(0.35 + bottleProgress * 0.65).toFixed(3)})`;

        if (bottleProgress > 0.72) revealed += 1;
      });

      instance.counter.textContent = `${String(revealed).padStart(2, '0')} / 05`;
    });
  };

  const requestUpdate = () => {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(update);
  };

  const init = (scope = document) => {
    if (reduceMotion.matches) return;

    const candidates = scope.matches?.(IMAGE_SELECTOR)
      ? [scope]
      : [...scope.querySelectorAll(IMAGE_SELECTOR)];

    candidates.forEach(build);
    requestUpdate();
  };

  window.CiranovaSampleScroll = { init };
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
